import { Moon, Sun } from 'lucide-react';
import { cn } from '../lib/cn';

export interface ThemeQuickToggleProps {
  /** The theme currently on screen (System already resolved to light/dark). */
  resolvedTheme: 'light' | 'dark';
  /** True when the saved preference is System — shown as a hint under the label. */
  followsSystem?: boolean;
  /** Called with the opposite explicit theme. */
  onToggle: (next: 'light' | 'dark') => void;
  /** `menuitemcheckbox` inside an ARIA menu with roving focus; `switch` everywhere else. */
  role?: 'switch' | 'menuitemcheckbox';
  tabIndex?: number;
  className?: string;
}

/** Light ↔ dark switch for hamburger menus. Sun and Moon both sit in the
 *  track; the brand-blue thumb covers whichever mode is on. System / Light /
 *  Dark stays in Settings (`ThemeModeSelector`). */
export function ThemeQuickToggle({
  resolvedTheme,
  followsSystem = false,
  onToggle,
  role = 'switch',
  tabIndex,
  className,
}: ThemeQuickToggleProps) {
  const isDark = resolvedTheme === 'dark';

  return (
    <button
      type="button"
      role={role}
      aria-checked={isDark}
      aria-label="Dark mode"
      tabIndex={tabIndex}
      data-theme-quick-toggle={resolvedTheme}
      onClick={() => onToggle(isDark ? 'light' : 'dark')}
      className={cn(
        'flex w-full min-h-[44px] items-center gap-3 rounded-lg px-3 text-left text-sm font-medium transition-colors',
        'text-[var(--color-on-surface)] hover:bg-[var(--color-surface-overlay)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60',
        className,
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span>Dark mode</span>
        {followsSystem && (
          <span className="text-xs font-normal text-[var(--color-on-subtle)]">Matching device</span>
        )}
      </span>
      <span
        aria-hidden
        className="relative inline-flex h-8 w-[60px] shrink-0 items-center rounded-full border"
        style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-page-bg)' }}
      >
        <span
          className={cn(
            'absolute top-[3px] left-[3px] h-6 w-6 rounded-full bg-[var(--color-brand)] shadow-sm transition-transform duration-200 ease-out',
            isDark && 'translate-x-[28px]',
          )}
        />
        <span className="relative z-10 flex w-full items-center justify-between px-[7px]">
          <Sun
            className={cn('h-4 w-4', isDark ? 'text-[var(--color-on-subtle)]' : 'text-white')}
          />
          <Moon
            className={cn('h-4 w-4', isDark ? 'text-white' : 'text-[var(--color-on-subtle)]')}
          />
        </span>
      </span>
    </button>
  );
}
